import { Dialog, Transition } from '@headlessui/react';
import React, { Fragment, useCallback, useState } from 'react';
import toast from 'react-hot-toast';
import { MdOutlineClose } from 'react-icons/md';
import { RiMegaphoneFill } from 'react-icons/ri';
import { TbCertificate, TbDeviceAnalytics, TbEdit, TbFileInvoice, TbPhoto, TbPlus, TbRosetteDiscount } from 'react-icons/tb';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { useFetchStripePortalSessionMutation, useFetchSubscriptionsQuery } from '@/api/hooks/useSubscription';

import { NavbarSubMenuItem } from '../Navbar/NavbarSubMenuItem';
import { Logo } from '@/components/atoms/Logo';
import { Typography } from '@/components/atoms/Typography';
import { NavMenu } from '@/components/molecules/NavMenu';

const links = [
  { name: 'ad', to: '/restaurant/subscription/:id', icon: <RiMegaphoneFill size={24} />, id: 5, special: true },
  { name: 'restaurantEdition', to: '/restaurant/:id', icon: <TbEdit size={24} />, id: 0 },
  { name: 'certificate', to: '/restaurant/certificate/:id', icon: <TbCertificate size={24} />, id: 1 },
  { name: 'restaurantPicture', to: '/restaurant/picture/:id', icon: <TbPhoto size={24} />, id: 2 },
  { name: 'promotion', to: '/restaurant/code-promo/:id', icon: <TbRosetteDiscount size={24} />, id: 3 },
  { name: 'stats', to: '/restaurant/stats/:id', icon: <TbDeviceAnalytics size={24} />, id: 4 },
  { name: 'invoices', invoiceTab: true, icon: <TbFileInvoice size={24} />, id: 6 }
];

interface Props {
  sidebarOpen: boolean;
  onChangeSidebar: (value: boolean) => void;
}

export const SideBar = ({ sidebarOpen, onChangeSidebar }: Props) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const restaurantId = id || '';

  if (!id) {
    navigate('/');
    return null;
  }

  const { mutate: fetchStripeSession } = useFetchStripePortalSessionMutation();
  const { data: subscriptionData } = useFetchSubscriptionsQuery({ restaurantId });
  const subscriptions = subscriptionData?.subscriptions || [];

  const customerId = subscriptions.find((subscription) => subscription.customerId)?.customerId;

  const handleManageSubscription = useCallback(() => {
    if (!customerId) {
      return toast.error("Aucune facture n'a été retrouvée.", {
        position: 'bottom-left',
        duration: 5000
      });
    }

    fetchStripeSession(customerId, {
      onSuccess: (data) => {
        window.open(data.url, '_blank');
      },
      onError: (error) => {
        console.error(error);
      }
    });
  }, [customerId, fetchStripeSession]);

  const updatedLinks = [...links];

  return (
    <>
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog as="div" className="fixed inset-0 flex z-40 md:hidden" onClose={() => onChangeSidebar(false)}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity  ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <div className="relative flex-1 flex flex-col max-w-xs w-full pb-small bg-white shadow-md">
              <Transition.Child
                as={Fragment}
                enter="ease-in-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in-out duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="absolute top-6 -right-16">
                  <button
                    type="button"
                    className="bg-black flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                    onClick={() => onChangeSidebar(false)}
                  >
                    <MdOutlineClose className="h-6 w-6 text-white" aria-hidden="true" />
                  </button>
                </div>
              </Transition.Child>
              <div className="flex-shrink-0 flex items-center h-20 p-3 bg-primary">
                <Logo />
              </div>
              <div className="mt-base flex-1 h-0 overflow-y-auto">
                <div className="flex flex-col px-3 space-y-1 md:hidden bottom-20 w-auto md:ml-5 gap-3">
                  <Link
                    to="/proposal-restaurant"
                    className="justify-center flex items-center gap-1 border border-gray-300 w-full rounded-full py-1 hover:bg-gray-100/20 duration-300 ease-in-out"
                  >
                    <TbPlus className="text-black " />
                    <Typography variant="h4" color="black">
                      Ajouter un restaurant
                    </Typography>
                  </Link>

                  <NavMenu
                    showOnPhone
                    links={updatedLinks.map((link) => ({
                      ...link,
                      // On remplace le :id éventuel
                      to: link.to?.replace(':id', id) || '#'
                    }))}
                    onLinkClick={(link) => {
                      if (link.invoiceTab) {
                        handleManageSubscription();
                      }
                    }}
                  />

                  <div className=" w-4/5 mx-auto h-5 border-t" />

                  <NavbarSubMenuItem />
                </div>
              </div>
            </div>
          </Transition.Child>
          <div className="flex-shrink-0 w-14" aria-hidden="true">
            {/* Dummy element to force sidebar to shrink to fit close icon */}
          </div>
        </Dialog>
      </Transition.Root>
      <NavMenu
        links={updatedLinks.map((link) => ({
          ...link,
          to: link.to?.replace(':id', id) || '#'
        }))}
        onLinkClick={(link) => {
          if (link.invoiceTab) {
            handleManageSubscription();
          }
        }}
      />
    </>
  );
};
